import React, { useEffect, useRef, useState } from 'react'
import UserContext, { UserContextState } from '../../UserContext'
import Cookies from 'js-cookie'
import { Callout } from 'react-foundation'
import PageHeading from '../Common/PageHeading'
import { ApiFetch } from '../Common/Api'
import { navigate } from '@reach/router'
import { DataLoading } from '../Common/Table'

type FakeSSOProps = { path: string }
type JWTProps = { 
    nameid: string
    sourcing_id: string
    email: string
}

const FakeSSO: React.FC<FakeSSOProps> = () => {
    const [fakeId, setFakeId] = useState(1)
    const [fakeEmail, setFakeEmail] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [userInfo, setUserInfo] = useState('')
    const [errorMsg, setErrorMsg] = useState('')
    const isMountedRef = useRef(true)
    const navigateTimeoutRef = useRef<number | null>(null)

    useEffect(() => {
        return () => {
            isMountedRef.current = false
            if (navigateTimeoutRef.current !== null) {
                window.clearTimeout(navigateTimeoutRef.current)
                navigateTimeoutRef.current = null
            }
        }
    }, [])

    const safeSetIsLoading = (value: boolean) => {
        if (isMountedRef.current) setIsLoading(value)
    }

    const safeSetUserInfo = (value: string) => {
        if (isMountedRef.current) setUserInfo(value)
    }

    const safeSetErrorMsg = (value: string) => {
        if (isMountedRef.current) setErrorMsg(value)
    }

    const parseJwt = (token: string):JWTProps => {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        }).join(''))
    
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return JSON.parse(jsonPayload)
    }

    const onLoginAsFakeUser = (context: UserContextState, roleKey: string) => {
        safeSetIsLoading(true)
        // eslint-disable-next-line no-console
        console.log('Fetching Impersonated User')
        ApiFetch<string>('SSO/Generate', { fakeUser: roleKey }, true).then(
            (result) => {
                if (!isMountedRef.current) return

                if (!result) {
                    safeSetIsLoading(false)
                    safeSetUserInfo('')
                    safeSetErrorMsg('Impersonation returned an empty token')
                    return
                }

                context.authenticateUser(result.indexOf('DOCTYPE') < 0 ? result : Cookies.get('cs_id_token'), (success) => {
                    if (!isMountedRef.current) return
                    safeSetIsLoading(false)

                    if (success) {
                        safeSetErrorMsg('')
                        const callback = sessionStorage.getItem('path-callback')
                        sessionStorage.removeItem('path-callback')
        
                        // eslint-disable-next-line no-console
                        navigateTimeoutRef.current = window.setTimeout(() => {
                            void navigate(callback || 'service-requests').catch(console.log)
                        }, 100)
                    }
                    else
                        safeSetErrorMsg('Error fetching user after impersonation, look for errors in SSO GET')
                })

                if (result.indexOf('DOCTYPE') < 0) {
                    const jwt = parseJwt(result)
                    safeSetUserInfo(`ID: ${jwt.sourcing_id} Name: ${jwt.nameid} Email: ${jwt.email}`)
                
                    // eslint-disable-next-line no-console
                    console.log(jwt)
                }
            },
            (e) => {
                if (!isMountedRef.current) return

                safeSetIsLoading(false)
                safeSetUserInfo('')
                safeSetErrorMsg('Error impersonating user')

                // eslint-disable-next-line no-console
                console.error('Error Fetching Impersonated User', e)
                //TODO redirect or show error?
            }
        )
    }

    return (
        <>
            <PageHeading pageTitle="Impersonation" createButton={false} filterButton={false} />

            <h4>Select Role to Impersonate</h4>

            <UserContext.Consumer>
                {
                    context =>
                        <>
                            {userInfo && <Callout color='success'>{userInfo}</Callout>}
                            {errorMsg && <Callout color='alert'>{errorMsg}</Callout>}
                            {isLoading ? <DataLoading /> : 
                                <>
                                    <div className='impersonation-buttons'>
                                        <button onClick={() => { onLoginAsFakeUser(context, 'Waiver Service Coordinator') }} className='button'>Waiver Service Coordinator</button>
                                        <button onClick={() => { onLoginAsFakeUser(context, 'Waiver Service Leader') }} className='button'>Waiver Service Leader</button>
                                        <button onClick={() => { onLoginAsFakeUser(context, 'Market Admin') }} className='button'>Market Admin</button>
                                        <button onClick={() => { onLoginAsFakeUser(context, 'Oversight Approver') }} className='button'>Oversight Approver</button>
                                        <button onClick={() => { onLoginAsFakeUser(context, 'Read Only All') }} className='button'>Read Only All</button>
                                        <button onClick={() => { onLoginAsFakeUser(context, 'IT Support') }} className='button'>IT Support</button>
                                        <button onClick={() => { onLoginAsFakeUser(context, 'Provider MI-2') }} className='button'>Provider (MI-2)</button>
                                        <button onClick={() => { onLoginAsFakeUser(context, 'Provider MI') }} className='button'>Provider (MI)</button>
                                        <button onClick={() => { onLoginAsFakeUser(context, 'Provider OH') }} className='button'>Provider (OH)</button>
                                        <button onClick={() => { onLoginAsFakeUser(context, 'self') }} className='button'>No Impersonation</button>
                                    </div>
                                    <hr />
                                    <div className='impersonation-user-select'>
                                        <h4>Impersonate a Specific User By existing ID</h4>
                                        <input type="text" value={fakeId ? fakeId : ''} onChange={(e) => {setFakeId(e.target.value !== '' ? parseInt(e.target.value) : 0)}} />
                                        <button onClick={() => { onLoginAsFakeUser(context, fakeId.toString()) }} className='button'>Specific User</button>
                                    </div>
                                    <hr />
                                    <div className='impersonation-user-select'>
                                        <h4>Impersonate a Specific User By Email</h4>
                                        <input type="text" value={fakeEmail ? fakeEmail : ''} onChange={(e) => {setFakeEmail(e.target.value)}} />
                                        <button onClick={() => { onLoginAsFakeUser(context, fakeEmail) }} className='button'>Specific User By Email</button>
                                    </div>
                                </>
                            }
                        </>
                }
            </UserContext.Consumer>
        </>
    )
}

export default FakeSSO
